<template>

    <footer v-if="!agency">
        <div class="container">
            <div class="row">
                <div class="col-12 col-md-4">
                    <div class="nav flex-column">
                        <div class="nav-item"><span class="nav-title">{{ $t('labels.product') }}</span></div>
                        <div class="nav-item"><a class="nav-link" href="#" @click.prevent="push('signup')">{{ $t('labels.get_started') }}</a></div>
                        <div class="nav-item"><a class="nav-link" :href="$demoUrl" target="_blank" rel="noopener noreferrer">{{ $t('labels.book_a_demo') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="#" @click.prevent="push('login')">{{ $t('labels.log_in') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="#" @click.prevent="push('pricing')">{{ $t('labels.pricing') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="#" @click.prevent="push('agencies')">{{ $t('labels.agencies') }}</a></div>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="nav flex-column">
                        <div class="nav-item"><span class="nav-title">{{ $t('labels.company') }}</span></div>
                        <div class="nav-item"><a class="nav-link" :href="$feedbackUrl" target="_blank">{{ $t('labels.roadmap') }}</a></div>
                        <div class="nav-item"><a class="nav-link" :href="`${$feedbackUrl}/changelog`" target="_blank">{{ $t('labels.changelog') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="https://blog.moregoodreviews.com" target="_blank">{{ $t('labels.blog') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="https://moregoodreviews.com/showcase/moregoodreviews" target="_blank">{{ $t('labels.our_reviews') }}</a></div>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="nav flex-column">
                        <div class="nav-item"><span class="nav-title">{{ $t('labels.support') }}</span></div>
                        <div class="nav-item"><a class="nav-link" href="#" @click.prevent="push('contact')">{{ $t('labels.contact_us') }}</a></div>
                        <div class="nav-item"><a class="nav-link" :href="$docsUrl" target="_blank">{{ $t('labels.documentation') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="https://discord.gg/9vvND2FygJ" target="_blank">{{ $t('labels.discord_community') }}</a></div>
                        <div class="nav-item"><a class="nav-link" href="https://status.moregoodreviews.com" target="_blank"><i class="fas fa-signal-stream mr-5"></i>{{ $t('labels.status') }}</a></div>
                    </div>
                </div>
            </div>
            <div class="support-email">
                <a href="mailto:support@moregoodreviews.com">support@moregoodreviews.com</a>
            </div>
            <div class="legal">
                <div class="legal-copy">© {{ year }} SJW Group, Inc.</div>
                <div class="legal-links">
                    <a
                        v-for="policy in policies"
                        :key="policy.key"
                        href="#"
                        @click.prevent="push(policy.key)"
                    >{{ $t(policy.label) }}</a>
                    <a
                        v-if="cookieConsentEnabled"
                        href="#"
                        @click.prevent="openCookiePreferences"
                    >{{ $t('labels.cookie_preference') }}</a>
                </div>
            </div>
        </div>
    </footer>

    <footer v-else-if="agency">
        <div class="container">
            <div class="row">
                <div class="col">
                    <span class="name mb-5">{{ agency.name }}</span>
                    <span class="mb-5" v-if="agency.footer" v-html="agency.footer" />
                    <span class="fs-10">© {{ year }}</span>
                </div>
            </div>
        </div>
    </footer>

</template>

<style scoped lang="scss">

    footer {
        @include font-medium(12px);
        background: $color-bg;
        color: $color-text;
        line-height: 1.6em;
        padding: 60px 20px 120px;
        text-align: center;
        > .container {
            max-width: 960px;
            padding-left: 0;
            padding-right: 0;
        }
        span {
            color: $color-text;
            display: block;
            &.name {
                @include font-semibold(12px);
            }
            a {
                color: $color-text;
                transition: opacity .3s;
                &:hover {
                    opacity: 0.5;
                }
            }
        }
        .support-email {
            @include font-semibold(14px);
            margin-top: 15px;
            text-align: center;
            a {
                color: $color-text;
                text-decoration: none;
                transition: opacity .3s;
                &:hover {
                    opacity: 0.5;
                }
            }
        }
        .legal {
            color: $color-muted;
            margin-top: 15px;
            text-align: center;
            width: 100%;
            .legal-copy {
                color: $color-muted;
                margin-bottom: 8px;
            }
            .legal-links {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: 6px 12px;
                justify-content: center;
            }
            a {
                color: $color-muted;
                text-decoration: underline;
                transition: opacity .3s;
                white-space: nowrap;
                &:hover {
                    opacity: 0.5;
                }
            }
        }
    }

    .nav {
        margin: 0 0 30px 0;
        text-align: center;
        .nav-item {
            margin: 0 0 10px 0;
            padding: 0;
        }
        .nav-title {
            @include font-semibold(14px);
            color: $color-text;
            line-height: 1.4em;
            margin: 0 0 5px 0;
        }
        .nav-link {
            @include font-medium(13px);
            display: inline;
            line-height: 1.4em;
            padding: 0;
        }
    }

</style>

<script>

import ConsentUtil from '@utils/ConsentUtil';
import LegalPolicies from '@utils/LegalPolicies';
import moment from 'moment';

export default {

    data() {
        return {
            policies: LegalPolicies,
            year: moment().format('Y'),
        }
    },

    computed: {

        apiKey() {
            return this.$store.getters.apiKey;
        },

        agency() {
            return this.$store.getters.agency;
        },

        cookieConsentEnabled() {
            return ConsentUtil.isRequired();
        },

    },

    methods: {

        push(name) {
            if (this.$route.name !== name) {
                this.$router.push({ name: name });
            }
        },

        openCookiePreferences() {
            this.$store.commit('setModal', 'cookie-preferences');
        },

    },

}

</script>